import type {
  VerifyIdentifierResult,
  VerifyResponse,
} from "@/lib/bibliography-verify";
import {
  compareReferenceMetadata,
  type MetadataFinding,
} from "@/lib/metadata-conflicts";
import {
  extractReferenceIdentifier,
  type ReferenceIdentifier,
} from "@/lib/reference-identifiers";
import type { TitleLookupResult } from "@/lib/reference-title-lookup";

/**
 * The reader-facing answer to "is this reference real?", combining the identifier pass
 * with the title search:
 *
 * - `verified`: an identifier resolved to the same work, or a title search found it.
 * - `mismatch`: a matching or similar work exists, but the written details (identifier,
 *   title, year or author) disagree with it — typical of an AI-mangled reference.
 * - `not-found`: the identifier does not resolve and no matching title was found —
 *   what a fabricated reference looks like, though not proof of one.
 * - `unchecked`: no lookup could be made or completed.
 */
export type ReferenceVerdict =
  | "verified"
  | "mismatch"
  | "not-found"
  | "unchecked";

export interface BibliographyReferenceResult {
  position: number;
  reference: string;
  status: "matched" | "review" | "unresolved" | "not-checked";
  identifier: ReferenceIdentifier | null;
  metadata: VerifyIdentifierResult["metadata"];
  findings: MetadataFinding[];
  error?: string;
  titleLookup: TitleLookupResult | null;
  verdict: ReferenceVerdict;
}

export interface BibliographyReport {
  summary: {
    references: number;
    identifiersFound: number;
    matched: number;
    needsReview: number;
    unresolved: number;
    notChecked: number;
    verified: number;
    mismatch: number;
    notFound: number;
    unchecked: number;
  };
  privacy: string;
  results: BibliographyReferenceResult[];
}

export function identifiersForReferences(references: string[]): string[] {
  const unique = new Map<string, string>();
  for (const reference of references) {
    const identifier = extractReferenceIdentifier(reference);
    if (identifier) {
      unique.set(`${identifier.kind}:${identifier.value}`, identifier.input);
    }
  }
  return [...unique.values()];
}

function privacyNote(identifiers: boolean, titles: boolean): string {
  if (titles) {
    return "Manuscript prose stayed in this browser. Extracted identifiers were sent for metadata lookup, and the titles of references that needed a search went straight from this browser to Crossref and OpenAlex.";
  }
  if (identifiers) {
    return "Reference and manuscript prose stayed in this browser. Only extracted identifiers were sent for metadata lookup.";
  }
  return "Reference and manuscript prose stayed in this browser. No metadata lookup was needed.";
}

/** Whether a reference's identifier pass leaves a question a title search can answer. */
export function needsTitleLookup(
  status: BibliographyReferenceResult["status"]
): boolean {
  return status !== "matched";
}

function verdictFor(
  status: BibliographyReferenceResult["status"],
  titleLookup: TitleLookupResult | null
): ReferenceVerdict {
  if (status === "matched") {
    return "verified";
  }
  if (status === "review") {
    return "mismatch";
  }
  if (!titleLookup?.ok) {
    return "unchecked";
  }
  if (titleLookup.verdict === "found" && status === "not-checked") {
    return "verified";
  }
  return titleLookup.verdict === "not-found" ? "not-found" : "mismatch";
}

function identifierKey(identifier: ReferenceIdentifier): string {
  return `${identifier.kind}:${identifier.value}`;
}

/**
 * Join identifier-only server results back to reference prose and compare metadata.
 * This function is client-safe and pure: the prose never needs to leave the browser.
 */
export function buildBibliographyReport(
  references: string[],
  lookup: VerifyResponse,
  titleLookups: ReadonlyMap<number, TitleLookupResult> = new Map()
): BibliographyReport {
  const resolvedByIdentifier = new Map(
    lookup.results.map((result) => [identifierKey(result.identifier), result])
  );
  const identifierResults = references.map(
    (
      reference,
      index
    ): Omit<BibliographyReferenceResult, "titleLookup" | "verdict"> => {
      const identifier = extractReferenceIdentifier(reference);
      if (!identifier) {
        return {
          position: index + 1,
          reference,
          status: "not-checked",
          identifier: null,
          metadata: null,
          findings: [],
          error:
            "No DOI, PMID, ISBN, or arXiv identifier was found. This reference was not checked against an external record.",
        };
      }

      const resolved = resolvedByIdentifier.get(identifierKey(identifier));
      if (!resolved?.metadata || resolved.status === "unresolved") {
        return {
          position: index + 1,
          reference,
          status: "unresolved",
          identifier,
          metadata: null,
          findings: [],
          error:
            resolved?.error ??
            "The identifier could not be resolved. This does not prove the source is invalid.",
        };
      }

      const findings = compareReferenceMetadata(reference, resolved.metadata);
      return {
        position: index + 1,
        reference,
        status: findings.some((finding) => finding.severity === "review")
          ? "review"
          : "matched",
        identifier,
        metadata: resolved.metadata,
        findings,
      };
    }
  );
  const results = identifierResults.map(
    (result): BibliographyReferenceResult => {
      const titleLookup = needsTitleLookup(result.status)
        ? (titleLookups.get(result.position) ?? null)
        : null;
      return {
        ...result,
        titleLookup,
        verdict: verdictFor(result.status, titleLookup),
      };
    }
  );
  const countVerdict = (verdict: ReferenceVerdict) =>
    results.filter((result) => result.verdict === verdict).length;

  return {
    summary: {
      references: results.length,
      identifiersFound: results.filter((result) => result.identifier !== null)
        .length,
      matched: results.filter((result) => result.status === "matched").length,
      needsReview: results.filter((result) => result.status === "review")
        .length,
      unresolved: results.filter((result) => result.status === "unresolved")
        .length,
      notChecked: results.filter((result) => result.status === "not-checked")
        .length,
      verified: countVerdict("verified"),
      mismatch: countVerdict("mismatch"),
      notFound: countVerdict("not-found"),
      unchecked: countVerdict("unchecked"),
    },
    privacy: privacyNote(lookup.results.length > 0, titleLookups.size > 0),
    results,
  };
}
